import { useState, useEffect, useMemo } from 'react'
import { UilPrint, UilMapMarker } from '@iconscout/react-unicons'
import {
  Row,
  Col,
  Input,
  Select,
  Button,
  Space,
  Switch,
  Divider,
  notification,
  Modal,
  Flex,
  Empty,
  Typography,
} from 'antd'
import PropTypes from 'prop-types'
import { Title, ExpandableTableCard, MetaInfo } from './styledComponents'
import {
  fetchClientMaterials,
  evoClient,
  putPart,
  fetchPart,
  fetchAllGroups,
  removePartsFromGroup,
} from '$api/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import CustomInputNumber from '$components/CustomInputNumber'
import TechnicalDrawingUpload from '$components/FileUpload/TechnicalDrawingUpload'
import Form from '$components/Form'
import { dateWithTimeRenderer } from '$utils/i18n'
import useMaterialMapping from '$utils/useMaterialMapping'
import { CheckCircle, TimesCircle, ExclamationCircle } from '$components/Icons'
import { CenteredSpinner } from '$components/Spinner'
import TruncateTag from '$components/TruncateTag'
import { useTheme } from 'styled-components'

const PartDetailForm = ({
  record,
  showEditDates = false,
  updateSelectedPart,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const [fcAttrHasChanged, setFcAttrHasChanged] = useState(false)
  const [selectedClientMaterial, setSelectedClientMaterial] = useState(null)
  const [
    isRemoveMaterialFromGroupModalVisible,
    setIsRemoveMaterialFromGroupModalVisible,
  ] = useState(false)
  const [groupToRemoveFrom, setGroupToRemoveFrom] = useState(null)

  const formPriceAM = Form.useWatch('Price AM', form)

  const { data: partData, isLoading: isLoadingPart } = useQuery({
    queryKey: ['partDetail', record.id],
    queryFn: () => fetchPart({ queryKey: ['part', record.id] }),
    initialData: record,
    enabled: !!record.id,
  })

  const { data: allDwhMaterials, isLoading: materialsAreLoading } =
    useMaterialMapping()
  const { data: clientMaterialsOptions, isLoading: isLoadingClientMaterials } =
    useQuery(
      ['clientMaterialsListForDropdown'],
      () =>
        fetchClientMaterials({
          queryKey: ['clientMaterialsListForDropdown', { page: 1, size: 100 }],
        }),
      {
        select: (data) => {
          if (!data || !Array.isArray(data.items)) return []
          return data.items.map((material) => ({
            value: material.id,
            label: material.name,
            name: material.name,
            mapped_dwh_material_ids: material.mapped_dwh_material_ids || [],
          }))
        },
      },
    )

  const clientMaterialsList = useMemo(() => {
    if (
      partData?.client_material &&
      clientMaterialsOptions != null &&
      !clientMaterialsOptions?.some(
        (material) => material.value === partData.client_material.id,
      )
    ) {
      return [
        {
          value: partData.client_material.id,
          label: partData.client_material.name,
          name: partData.client_material.name,
          mapped_dwh_material_ids:
            partData.client_material.mapped_dwh_material_ids || [],
          disabled: true,
        },
        ...clientMaterialsOptions,
      ]
    }
    return clientMaterialsOptions
  }, [clientMaterialsOptions])

  useEffect(() => {
    if (partData) {
      form.setFieldsValue({
        ...partData,
        ...partData.float_attributes.reduce((acc, attr) => {
          acc[attr.key] = attr.value
          return acc
        }, {}),
        ...partData.attributes.reduce((acc, attr) => {
          acc[attr.key] = isTrue(attr.value)
          return acc
        }, {}),
        client_material_id: partData.client_material?.id,
      })
    }
  }, [partData, form])

  const updateGroupMutation = useMutation(
    (updatedPart) => {
      return evoClient.put(putPart + record.id, JSON.stringify(updatedPart), {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    {
      onSuccess: async () => {
        try {
          openNotification('top', t('part_updated'))

          const feasibilityCheckAttr = record.attributes.find(
            (attr) => attr.key === 'Feasibility Check',
          )?.value
          if (
            fcAttrHasChanged &&
            (feasibilityCheckAttr === 'true' ||
              feasibilityCheckAttr === 'false')
          ) {
            openWarning('top', t('service.feasibility_check.fc_reset_message'))

            form.setFieldsValue({
              'Feasibility Check': false,
              'Technical Suitability': false,
            })

            await Promise.all([
              queryClient.invalidateQueries(['bookedServices']),
              queryClient.invalidateQueries(['orderPartsLists']),
              queryClient.invalidateQueries(['allparts']),
              queryClient.invalidateQueries(['partKpis']),
            ])
          }

          await queryClient.invalidateQueries(['parts'])
          await queryClient.invalidateQueries({
            queryKey: ['clientMaterialsList'],
          })
          const updatedPartData = await queryClient.fetchQuery(
            ['partDetail', record.id],
            () => fetchPart({ queryKey: ['part', record.id] }),
          )
          if (updateSelectedPart && updatedPartData) {
            updateSelectedPart(updatedPartData)
          }
        } catch (error) {
          console.error('Error during onSuccess:', error)
        }
      },
    },
  )

  const { data: groupsData } = useQuery(['allgroups'], fetchAllGroups)

  const removeFromGroupMutation = useMutation({
    mutationFn: removePartsFromGroup,
    onSuccess: () => {
      queryClient.invalidateQueries(['allgroups'])
      queryClient.invalidateQueries(['groupParts', groupToRemoveFrom.id])
      queryClient.invalidateQueries(['parts'])
    },
    onError: (err) => {
      console.error('Error removing part from group:', err)
    },
  })

  const handleRemovePartFromGroup = () => {
    removeFromGroupMutation.mutate({
      partsIds: [partData.id],
      groupId: groupToRemoveFrom.id,
    })
    setIsRemoveMaterialFromGroupModalVisible(false)
    setGroupToRemoveFrom(null)
  }

  const handleCancelRemovingPartFromGroup = () => {
    setIsRemoveMaterialFromGroupModalVisible(false)
    setGroupToRemoveFrom(null)
  }

  let partwidth = record.float_attributes.find((x) => x.key === 'Width')?.value
  let partheight = record.float_attributes.find(
    (x) => x.key === 'Height',
  )?.value
  let partdepth = record.float_attributes.find((x) => x.key === 'Depth')?.value
  let partweight = record.float_attributes.find(
    (x) => x.key === 'Weight',
  )?.value

  let partmaterial_id = record?.client_material?.id
  let partprice = record.float_attributes.find((x) => x.key === 'Price')?.value
  let partprice_am_is_expired = useMemo(() => {
    const priceAm = record.float_attributes.find((x) => x.key === 'Price AM')
    return (
      (priceAm?.description ?? '').includes('old') &&
      priceAm.value === formPriceAM
    )
  }, [record, formPriceAM])
  let partprice_am = record.float_attributes.find(
    (x) => x.key === 'Price AM',
  )?.value
  let partprinting_license = record.attributes.find(
    (x) => x.key === 'Printing License',
  )?.value
  let partfeasibility_check = record.attributes.find(
    (x) => x.key === 'Feasibility Check',
  )?.value
  let parteco_suit = record.attributes.find(
    (x) => x.key === 'Economical Suitability',
  )?.value
  let parttech_suit = record.attributes.find(
    (x) => x.key === 'Technical Suitability',
  )?.value

  const isTrue = (value) => {
    if (typeof value === 'string') {
      value = value.trim().toLowerCase()
    }
    switch (value) {
      case true:
      case 'true':
      case 1:
      case '1':
        return true
      default:
        return false
    }
  }

  const onFinish = async (values) => {
    const updatedPart = JSON.parse(JSON.stringify(partData))
    let fcShouldBeInvalidated = false
    let materialChanged = false
    let oldMaterialName = null

    updatedPart.name = values.name
    updatedPart.id_part_client = values.id_part_client

    if (
      values.Width !==
        partData.float_attributes.find((a) => a.key === 'Width')?.value ||
      values.Height !==
        partData.float_attributes.find((a) => a.key === 'Height')?.value ||
      values.Depth !==
        partData.float_attributes.find((a) => a.key === 'Depth')?.value ||
      values.Weight !==
        partData.float_attributes.find((a) => a.key === 'Weight')?.value ||
      values.client_material_id !== partData.client_material?.id
    ) {
      fcShouldBeInvalidated = true
    }

    if (values.client_material_id !== partData.client_material?.id) {
      materialChanged = true
      oldMaterialName = partData.client_material?.name
    }

    const updateAttribute = (arr, key, value) => {
      const index = arr.findIndex((attr) => attr.key === key)
      if (value === null || value === undefined) {
        if (index > -1) arr.splice(index, 1)
      } else {
        if (index > -1) {
          arr[index].value = value
        } else {
          arr.push({ key, value, description: null })
        }
      }
    }

    updateAttribute(updatedPart.float_attributes, 'Width', values.Width)
    updateAttribute(updatedPart.float_attributes, 'Depth', values.Depth)
    updateAttribute(updatedPart.float_attributes, 'Height', values.Height)
    updateAttribute(updatedPart.float_attributes, 'Weight', values.Weight)
    updateAttribute(updatedPart.float_attributes, 'Price', values.Price)
    updateAttribute(
      updatedPart.float_attributes,
      'Price AM',
      values['Price AM'],
    )
    updateAttribute(
      updatedPart.attributes,
      'Printing License',
      values['Printing License']?.toString(),
    )
    updateAttribute(
      updatedPart.attributes,
      'Economical Suitability',
      values['Economical Suitability']?.toString(),
    )

    if (values.client_material_id !== undefined) {
      updatedPart.client_material_id = values.client_material_id

      if (selectedClientMaterial) {
        updateAttribute(
          updatedPart.attributes,
          'Material',
          selectedClientMaterial.name,
        )
        updatedPart.dwh_materials =
          selectedClientMaterial.mapped_dwh_material_ids || []
      } else {
        updatedPart.attributes = updatedPart.attributes.filter(
          (attr) => attr.key !== 'Material',
        )
        updatedPart.dwh_materials = []
        updatedPart.client_material = null
      }
    } else {
      updatedPart.attributes = updatedPart.attributes.filter(
        (attr) => attr.key !== 'Material',
      )
      updatedPart.dwh_materials = []
      updatedPart.client_material_id = null
    }

    if (
      values.Width !== partwidth ||
      values.Height !== partheight ||
      values.Depth !== partdepth ||
      values.Weight !== partweight ||
      values.client_material_id !== partmaterial_id
    ) {
      setFcAttrHasChanged(true)
    }

    if (fcShouldBeInvalidated && updatedPart.dwh_materials_fc_check) {
      updatedPart.dwh_materials_fc_check = {}
      updatedPart.fc_invalidation_reason = 'data_changed'
      let filteredAttributes = updatedPart.attributes.filter(
        (item) =>
          item.key !== 'Feasibility Check' &&
          item.key !== 'Technical Suitability',
      )
      updatedPart.attributes = filteredAttributes
    }

    if (
      materialChanged &&
      oldMaterialName &&
      groupsData &&
      partData.part_lists
    ) {
      const matchingGroup = groupsData.find(
        (group) => group.list_name === oldMaterialName,
      )

      if (matchingGroup && partData.part_lists.includes(matchingGroup.id)) {
        setGroupToRemoveFrom(matchingGroup)
        setIsRemoveMaterialFromGroupModalVisible(true)
      }
    }

    updateGroupMutation.mutate(updatedPart)
  }

  const onReset = () => {
    openNotification('top', 'Änderungen verworfen')
    form.resetFields()
  }

  const openNotification = (placement, message) => {
    notification.success({
      message,
      description: ``,
      placement,
    })
  }

  const openWarning = (placement, message) => {
    notification.warning({
      message,
      description: ``,
      placement,
    })
  }

  const getFeasibilityTitle = (isFeasible) => {
    if (isFeasible === true) return t('fc_feasible')
    if (isFeasible === false) return t('fc_not_feasible')
    return t('fc_not_checked')
  }

  const getFeasibilityIcon = (isFeasible) => {
    if (isFeasible === true) return <CheckCircle />
    if (isFeasible === false) return <TimesCircle />
    return <ExclamationCircle />
  }

  if (isLoadingPart || materialsAreLoading) {
    return <CenteredSpinner />
  }

  return (
    <ExpandableTableCard>
      {showEditDates && (
        <MetaInfo>
          <span className="updated_at">
            {t('Modification Date')}:{' '}
            {dateWithTimeRenderer(t)(record.updated_at)}
          </span>
          <span className="created_at">
            {t('Creation Date')}: {dateWithTimeRenderer(t)(record.created_at)}
          </span>
        </MetaInfo>
      )}
      <Title>{t('parts_master_data.edit_part')}</Title>
      <Form
        layout="horizontal"
        size="small"
        onFinish={onFinish}
        onReset={onReset}
        form={form}
        className="partdetailform"
        labelAlign="left"
      >
        <Row className="row">
          <Col
            flex="1 0 30%"
            className="formcolumn"
            style={{ maxWidth: '300px' }}
          >
            <Form.Item
              label={t('Part No')}
              name="id_part_client"
              initialValue={record.id_part_client}
              width="160px"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Input
                disabled={true}
                variant="borderless"
                className="white-bg"
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('marketplace.detail_form.name.missing'),
                },
              ]}
              label={t('Part Name')}
              name="name"
              initialValue={record.name}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Input variant="borderless" className="white-bg" />
            </Form.Item>
            <Typography.Title style={{ fontSize: '12px' }}>
              {t('Dimensions')}
            </Typography.Title>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('marketplace.detail_form.width.missing'),
                },
              ]}
              label={t('Width')}
              name="Width"
              layout="inline"
              initialValue={partwidth}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <CustomInputNumber
                addonAfter="mm"
                min={0.01}
                step={0.01}
                variant="borderless"
                className="white-bg"
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('marketplace.detail_form.depth.missing'),
                },
              ]}
              label={t('Depth')}
              name="Depth"
              initialValue={partdepth}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <CustomInputNumber
                addonAfter="mm"
                min={0.01}
                step={0.01}
                variant="borderless"
                className="white-bg"
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('marketplace.detail_form.height.missing'),
                },
              ]}
              label={t('Height')}
              name="Height"
              initialValue={partheight}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
              style={{ marginBottom: '24px' }}
            >
              <CustomInputNumber
                addonAfter="mm"
                min={0.01}
                step={0.01}
                variant="borderless"
                className="white-bg"
              />
            </Form.Item>
            <Form.Item
              label={t('Weight')}
              name="Weight"
              initialValue={partweight}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
              style={{ marginBottom: '24px' }}
            >
              <CustomInputNumber
                addonAfter="g"
                min={0.01}
                step={0.01}
                variant="borderless"
                className="white-bg"
              />
            </Form.Item>
            <Form.Item
              label={t('client_materials_header', 'My Internal Material')}
              name="client_material_id"
              initialValue={record?.client_material?.id}
            >
              <Select
                allowClear
                showSearch
                variant="borderless"
                className="white-bg"
                placeholder={t('parts_master_data.select_material_placeholder')}
                loading={isLoadingClientMaterials}
                options={clientMaterialsList}
                style={{ minWidth: '66px' }}
                popupMatchSelectWidth={200}
                optionFilterProp="label"
                onClear={() => setSelectedClientMaterial(null)}
                onChange={(_value, option) =>
                  setSelectedClientMaterial(option || null)
                }
              ></Select>
            </Form.Item>
            <Form.Item
              label={t('dwh_materials_header') + ' :'}
              colon={false}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24, style: { alignItems: 'flex-start' } }}
              style={{ marginBottom: '24px' }}
            >
              {(partData.client_material?.mapped_dwh_material_ids || [])
                .length === 0 ? (
                <Empty description={t('no_mapped_materials')} />
              ) : (
                <Flex gap="small" wrap>
                  {partData.client_material?.mapped_dwh_material_ids.map(
                    (dwhId) => {
                      const material = (allDwhMaterials || []).find(
                        (m) => String(m.id) === String(dwhId),
                      )
                      const materialName = material
                        ? material.name
                        : `ID: ${String(dwhId)}`

                      let isFeasible =
                        partData.dwh_materials_fc_check?.[String(dwhId)]

                      const icon = getFeasibilityIcon(isFeasible)
                      const title = getFeasibilityTitle(isFeasible)
                      return (
                        <TruncateTag
                          key={dwhId}
                          icon={icon}
                          text={materialName}
                          addedTooltip={title}
                          iconWidth={25}
                        />
                      )
                    },
                  )}
                </Flex>
              )}
            </Form.Item>
          </Col>
          <Divider type="vertical" className="partsdivider" />
          <Col flex="1 0 25%" className="formcolumn">
            <Form.Item
              label={t('Price')}
              name="Price"
              initialValue={partprice}
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
            >
              <CustomInputNumber
                addonAfter="€"
                min={0.01}
                max={10000}
                step={0.01}
                variant="borderless"
                className="white-bg"
              />
            </Form.Item>
            <Form.Item
              label={t('Price AM')}
              name="Price AM"
              initialValue={partprice_am}
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
            >
              <CustomInputNumber
                addonAfter="€"
                min={0.01}
                max={10000}
                step={0.01}
                variant="borderless"
                className="white-bg"
                showTooltip={true}
                tooltipTitle={
                  partprice_am_is_expired
                    ? record?.client_material?.is_archived
                      ? t('parts_master_data.material_archived_priceam')
                      : t('parts_master_data.material_changed_priceam')
                    : null
                }
                status={partprice_am_is_expired ? 'error' : ''}
                style={{
                  border: partprice_am_is_expired
                    ? `1px solid ${theme.colorBrightRed500 ?? 'red'}`
                    : '',
                }}
              />
            </Form.Item>
            <TechnicalDrawingUpload record={record} />
          </Col>
          <Divider type="vertical" className="partsdivider" />
          <Col flex="1 0 25%" className="formcolumn">
            <Form.Item
              label={t('Available Printers')}
              labelCol={{ span: 18 }}
              wrapperCol={{ span: 6 }}
            >
              <Button shape="round">
                <UilPrint size="18" />
                <UilMapMarker size="18" />
              </Button>
            </Form.Item>
            <Form.Item
              label={t('Printing License')}
              style={{ alignItems: 'flex-end' }}
              name="Printing License"
              valuePropName="checked"
              labelCol={{ span: 18 }}
              wrapperCol={{ span: 6 }}
            >
              <Switch defaultChecked={isTrue(partprinting_license)} />
            </Form.Item>
            <Form.Item
              label={t('parts_master_data.feasibility_check')}
              name="Feasibility Check"
              valuePropName="checked"
              labelCol={{ span: 18 }}
              wrapperCol={{ span: 6 }}
            >
              <Switch
                defaultChecked={isTrue(partfeasibility_check)}
                disabled={true}
              />
            </Form.Item>
            <Form.Item
              label={t('parts_master_data.economic_suitability')}
              name="Economical Suitability"
              valuePropName="checked"
              labelCol={{ span: 18 }}
              wrapperCol={{ span: 6 }}
            >
              <Switch defaultChecked={isTrue(parteco_suit)} />
            </Form.Item>
            <Form.Item
              label={t('parts_master_data.technical_suitability')}
              name="Technical Suitability"
              valuePropName="checked"
              labelCol={{ span: 18 }}
              wrapperCol={{ span: 6 }}
            >
              <Switch defaultChecked={isTrue(parttech_suit)} disabled={true} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item style={{ float: 'right', marginTop: '20px' }}>
          <Space>
            <Button type="secondary" onClick={onReset}>
              {t('button.discard')}
            </Button>
            <Button type="primary" htmlType="submit">
              {t('button.submit_changes')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
      <Modal
        title={t('parts_master_data.part_remove_from_group_confirm', {
          groupName: groupToRemoveFrom?.list_name,
        })}
        open={isRemoveMaterialFromGroupModalVisible}
        okText={t('parts_master_data.remove_from_group_button')}
        cancelText={t('parts_master_data.keep_in_group_button')}
        onOk={handleRemovePartFromGroup}
        onCancel={handleCancelRemovingPartFromGroup}
      />
    </ExpandableTableCard>
  )
}

PartDetailForm.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  showEditDates: PropTypes.bool,
  updateSelectedPart: PropTypes.func,
}

export default PartDetailForm
